import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Target, Users, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">About ICT TechSolutions</h1>
          <p className="text-xl text-gray-600">
            Your trusted IT partner in Melbourne, dedicated to empowering small businesses with enterprise-grade technology solutions.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Founded in Melbourne, ICT TechSolutions was born from a simple observation: small businesses deserve the same level of IT expertise and support as large enterprises, without the enterprise price tag.
                </p>
                <p>
                  With years of experience in IT consulting, our mission remains the same - to make cutting-edge technology accessible and manageable for businesses of all sizes. We specialize in making technology hassle-free.
                </p>
                <p>
                  Today, we're proud to serve clients across Melbourne and beyond, helping them leverage Microsoft 365, Azure, and Google Workspace to transform their operations and achieve their business goals.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-blue-50 to-white shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Target className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Our Mission</h3>
                      <p className="text-gray-600">
                        To empower small businesses with enterprise-grade technology solutions that are accessible, affordable, and backed by exceptional support.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-gray-50 to-white shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Heart className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Our Values</h3>
                      <p className="text-gray-600">
                        Integrity, expertise, and customer-first service. We believe in building long-term partnerships, not just completing projects.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Businesses Choose Us</h2>
            <p className="text-xl text-gray-600">What sets us apart from other IT consultancies</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Award,
                title: 'Certified Experts',
                description: 'Microsoft, Azure, and Google certified professionals with years of real-world experience.'
              },
              {
                icon: Users,
                title: 'Local Support',
                description: 'Based in Melbourne, we provide personalized, face-to-face service when you need it.'
              },
              {
                icon: Target,
                title: 'Business Focused',
                description: 'We understand small business challenges and tailor solutions to your specific needs.'
              },
              {
                icon: Heart,
                title: 'Long-term Partnership',
                description: 'We\'re not just service providers - we\'re your technology partners for the long haul.'
              }
            ].map((item, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="text-blue-600" size={28} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Partner with Us?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Let's discuss how we can help your business thrive with the right technology solutions.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl hover:shadow-2xl transition-all duration-200">
              Get in Touch
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
